<template>
  <aside class="reports-sidebar">
    <section class="sidebar-section">
      <h3 class="sidebar-heading">Favorites</h3>
      <ul v-if="reportsStore.favoriteReports.length > 0" class="sidebar-list">
        <li
          v-for="report in reportsStore.favoriteReports"
          :key="report.id"
          class="sidebar-item"
        >
          <button type="button" class="sidebar-report-btn" @click="viewReport(report)">
            <span class="sidebar-report-name">{{ report.displayName }}</span>
          </button>
          <div class="sidebar-menu">
            <button
              type="button"
              class="sidebar-kebab"
              @click.stop="toggleFavMenu(report.id)"
            >&#8942;</button>
            <div v-if="openFavMenuId === report.id" class="sidebar-dropdown">
              <button type="button" class="sidebar-dropdown-item" @click="viewReport(report)">View</button>
              <button type="button" class="sidebar-dropdown-item" @click="unfavoriteReport(report)">Unfavorite</button>
            </div>
          </div>
        </li>
      </ul>
      <p v-else class="sidebar-empty">No favorites yet.</p>
    </section>

    <section class="sidebar-section">
      <h3 class="sidebar-heading">Recently Viewed</h3>
      <ul v-if="reportsStore.recentlyViewed.length > 0" class="sidebar-list">
        <li
          v-for="entry in reportsStore.recentlyViewed"
          :key="entry.id"
          class="sidebar-item"
        >
          <button type="button" class="sidebar-report-btn" @click="viewById(entry.id)">
            <span class="sidebar-report-name">{{ entry.displayName }}</span>
          </button>
          <div class="sidebar-menu">
            <button
              type="button"
              class="sidebar-kebab"
              @click.stop="toggleRecentMenu(entry.id)"
            >&#8942;</button>
            <div v-if="openRecentMenuId === entry.id" class="sidebar-dropdown">
              <button type="button" class="sidebar-dropdown-item" @click="viewById(entry.id)">View</button>
            </div>
          </div>
        </li>
      </ul>
      <p v-else class="sidebar-empty">Nothing viewed yet.</p>
    </section>
  </aside>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { useReportsStore } from "@/store/store/reportsStore";
import type { ReportItem } from "@/types/reports";

const reportsStore = useReportsStore();

const emit = defineEmits<{
  (e: "open-report", report: ReportItem): void;
  (e: "open-by-id", id: number): void;
}>();

const openFavMenuId = ref<number | null>(null);
const openRecentMenuId = ref<number | null>(null);

function toggleFavMenu(id: number) {
  openFavMenuId.value = openFavMenuId.value === id ? null : id;
  openRecentMenuId.value = null;
}

function toggleRecentMenu(id: number) {
  openRecentMenuId.value = openRecentMenuId.value === id ? null : id;
  openFavMenuId.value = null;
}

function closeMenus() {
  openFavMenuId.value = null;
  openRecentMenuId.value = null;
}

function viewReport(report: ReportItem) {
  emit("open-report", report);
  closeMenus();
}

function viewById(id: number) {
  emit("open-by-id", id);
  closeMenus();
}

function unfavoriteReport(report: ReportItem) {
  reportsStore.toggleFavorite(report);
  closeMenus();
}

onMounted(() => document.addEventListener("click", closeMenus));
onUnmounted(() => document.removeEventListener("click", closeMenus));
</script>

<style scoped>
.reports-sidebar {
  width: 240px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.sidebar-section {
  background: #f5f5f5;
  border-radius: 4px;
  padding: 14px 16px;
}

.sidebar-heading {
  font-size: 13px;
  font-weight: 700;
  color: #555;
  margin: 0 0 10px;
}

.sidebar-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.sidebar-item {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e8e8e8;
}

.sidebar-item:last-child {
  border-bottom: none;
}

.sidebar-report-btn {
  display: flex;
  align-items: center;
  flex: 1;
  padding: 7px 2px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: #1a4d8f;
  text-align: left;
  min-width: 0;
}

.sidebar-report-btn:hover {
  background: #ebebeb;
}

.sidebar-report-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-menu {
  position: relative;
  flex-shrink: 0;
}

.sidebar-kebab {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #aaa;
  padding: 4px 6px;
  line-height: 1;
  border-radius: 3px;
}

.sidebar-kebab:hover {
  background: #e0e0e0;
  color: #555;
}

.sidebar-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 2px);
  min-width: 120px;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 100;
  overflow: hidden;
}

.sidebar-dropdown-item {
  display: block;
  width: 100%;
  padding: 8px 14px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  text-align: left;
  white-space: nowrap;
}

.sidebar-dropdown-item:hover {
  background: #f0f0f0;
}

.sidebar-empty {
  font-size: 13px;
  color: #aaa;
  margin: 0;
}
</style>
